import api from './api';

export interface DatabaseStats {
  general: {
    dbName: string;
    collections: number;
    objects: number;
    dataSize: number;
    storageSize: number;
    indexSize: number;
    fileSize: number;
    avgObjSize: number;
  };
  collections: Array<{
    name: string;
    documents: number;
    size: number;
    storageSize: number;
    avgObjSize: number;
    indexes: number;
    indexSize: number;
  }>;
  connectionStatus: string;
  mongoVersion: string;
}

export interface DatabaseHealth {
  connectionStatus: 'healthy' | 'unhealthy' | 'error';
  indexIssues: unknown[];
  lastCheck: string;
  error?: string;
}

export interface BackupInfo {
  key: string;
  name: string;
  size: number;
  lastModified: string;
  type: 'full' | 'incremental';
}

export interface CreateBackupRequest {
  type: 'full' | 'incremental';
  description?: string;
}

export interface RestoreBackupRequest {
  backupKey: string;
}

export interface BackupProgress {
  id: string;
  name: string;
  type: 'full' | 'incremental';
  description: string;
  status: 'starting' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  phase: 'initialization' | 'database_export' | 'compression' | 'upload' | 'cleanup' | 'completed';
  startTime: string;
  endTime: string | null;
  size: number;
  collections: string[];
  currentCollection: string | null;
  error: string | null;
  details: Array<{
    timestamp: string;
    message: string;
  }>;
}

export interface BackupStats {
  totalBackups: number;
  totalSize: number;
  totalSizeFormatted: string;
  sizeLimit: number;
  sizeLimitFormatted: string;
  sizeUsagePercentage: number;
  backupsByType: {
    full: number;
    incremental: number;
  };
  retentionPolicies: {
    daily: number;
    weekly: number;
    monthly: number;
    maxSize: number;
  };
  activeBackups: number;
  recentBackups: number;
  activeBackupsList: BackupProgress[];
  recentBackupsList: BackupProgress[];
  systemStatus: {
    isBackupRunning: boolean;
    lastBackupTime: string | null;
    lastBackupStatus: string | null;
  };
}

class DatabaseService {
  // Obtener estadísticas de la base de datos
  async getDatabaseStats(): Promise<DatabaseStats> {
    const response = await api.get('/admin/database/stats');
    return response.data.data;
  }

  // Verificar salud de la base de datos
  async checkDatabaseHealth(): Promise<DatabaseHealth> {
    const response = await api.get('/admin/database/health');
    return response.data.data;
  }

  // Listar backups disponibles
  async listBackups(): Promise<BackupInfo[]> {
    const response = await api.get('/admin/database/backups');
    return response.data.data;
  }

  // Crear backup de la base de datos
  async createBackup(data: CreateBackupRequest): Promise<{ message: string; status: string; backupId: string }> {
    const response = await api.post('/admin/database/backups', data);
    return response.data;
  }

  // Restaurar backup
  async restoreBackup(data: RestoreBackupRequest): Promise<{ message: string; status: string }> {
    const response = await api.post('/admin/database/restore', data);
    return response.data;
  }

  // Eliminar backup
  async deleteBackup(backupKey: string): Promise<{ message: string }> {
    const response = await api.delete(`/admin/database/backups/${encodeURIComponent(backupKey)}`);
    return response.data.data;
  }

  // Obtener backups activos con progreso
  async getActiveBackups(): Promise<BackupProgress[]> {
    const response = await api.get('/admin/database/active-backups');
    return response.data.data;
  }

  // Obtener historial de backups
  async getBackupHistory(): Promise<BackupProgress[]> {
    const response = await api.get('/admin/database/backup-history');
    return response.data.data;
  }

  // Obtener progreso de backup específico
  async getBackupProgress(backupId: string): Promise<BackupProgress> {
    const response = await api.get(`/admin/database/backup-progress/${backupId}`);
    return response.data.data;
  }

  // Cancelar backup activo
  async cancelBackup(backupId: string): Promise<{ message: string }> {
    const response = await api.post(`/admin/database/cancel-backup/${backupId}`);
    return response.data;
  }

  // Obtener estadísticas completas con progreso
  async getBackupStatsWithProgress(): Promise<BackupStats> {
    const response = await api.get('/admin/database/backup-stats-with-progress');
    return response.data.data;
  }

  // Obtener políticas de retención
  async getRetentionPolicies(): Promise<BackupStats['retentionPolicies']> {
    const response = await api.get('/admin/database/retention-policies');
    return response.data.data;
  }

  // Actualizar políticas de retención
  async updateRetentionPolicies(policies: Partial<BackupStats['retentionPolicies']>): Promise<BackupStats['retentionPolicies']> {
    const response = await api.put('/admin/database/retention-policies', policies);
    return response.data.data;
  }

  // Aplicar políticas de retención manualmente
  async applyRetentionPolicies(): Promise<{ totalBackups: number; deletedCount: number }> {
    const response = await api.post('/admin/database/apply-retention');
    return response.data.data;
  }

  // Formatear tamaño en bytes a formato legible
  formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Formatear fecha de manera legible
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }
}

export default new DatabaseService();